import { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectHasPermission,
} from "../../store/slices/authSlice";
import {
  useGetAppointmentsQuery,
  useGetAbsencesQuery,
} from "../../store/services/calendarApi";
import { toLocalDateStr, canSeeAbsenceDetails } from "../../utils/absenceUtils";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import { isAssignableTherapist } from "../../config/permissionsRegistry";
import { useToast } from "../../contexts/ToastContext";
import { ReadOnlyBanner } from "../shared/access";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import {
  useVisibilityRefresh,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import DayView from "./DayView";
import WeekView from "./WeekView";
import MonthView from "./MonthView";
import AppointmentModal from "./AppointmentEntryModal";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  UsersThree,
  ArrowClockwise,
  Plus,
  PlusCircle,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import ContentLoader from "../shared/ContentLoader";
import CustomClinikitActionButton from "../ui/CustomClinikitActionButton";
import "./ClinicCalendarModal.css";

const MOBILE_BREAKPOINT = 768;

const VIEW_OPTIONS = [
  { value: "day", label: "יום" },
  { value: "week", label: "שבוע" },
  { value: "month", label: "חודש" },
];

// Visually distinctive colors for therapists. Order matters for stable
// assignment: the nth active therapist gets the nth color.
const THERAPIST_COLORS = [
  "#4dd9b4", // Primary turquoise
  "#e91e63", // Pink
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#3f51b5", // Indigo
  "#00bcd4", // Cyan
  "#f44336", // Red
  "#8bc34a", // Light green
  "#795548", // Brown
  "#607d8b", // Blue grey
  "#ffc107", // Amber
  "#ab47bc", // Light purple
];

/**
 * ClinicCalendarModal — full clinic-wide calendar for the admin.
 *
 * Shows all clinic appointments across all therapists in day / week / month
 * view. Every appointment card displays the therapist name alongside the
 * patient, color-coded by therapist so the admin can quickly see who is
 * meeting whom. Clicking an empty slot opens the AppointmentModal in "admin
 * scheduling" mode (therapist dropdown + patient dropdown for the selected
 * therapist). Clicking an existing appointment opens it for view / edit.
 *
 * Multi-clinic v2:
 *  - `embedded` renders the same internals as a page section (no fixed
 *    overlay, no close button, no body-scroll lock) — used by
 *    `/dashboard/clinic-calendar` (ClinicCalendarPage).
 *  - Non-admins reach it via `calendar.viewClinic`. Without
 *    `calendar.manageOthersAppointments` the calendar is read-only: slot
 *    clicks are no-ops and appointment clicks only toast. Admins keep the
 *    full flow unchanged.
 *
 * Error Code range: CK-3600 – CK-3609
 */
function ClinicCalendarModal({ onClose, initialView = "week", embedded = false }) {
  const clinicId = useSelector(selectClinicId);
  const adminTherapistId = useSelector(selectTherapistId);
  const adminTherapistName = useSelector(selectTherapistName);
  const viewerIsAdmin = useSelector(selectIsAdmin);
  const canManageOthersAppointments = useSelector(
    selectHasPermission("calendar.manageOthersAppointments")
  );
  const canManage = viewerIsAdmin || canManageOthersAppointments === true;
  const { showInfo } = useToast();

  const [viewMode, setViewMode] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [prefilledDateOnly, setPrefilledDateOnly] = useState(false);
  const [hiddenTherapistIds, setHiddenTherapistIds] = useState(() => new Set());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile && viewMode === "week") setViewMode("day");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Lock body scroll while the modal is open so the outer calendar doesn't
  // scroll underneath. Not applicable when rendered as a page section.
  useEffect(() => {
    if (embedded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [embedded]);

  // Close on Escape — only if the inner AppointmentModal isn't open, since
  // AppointmentModal has its own escape handling (and unsaved-change warning).
  useEffect(() => {
    if (embedded || typeof onClose !== "function") return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape" && !showAppointmentModal) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, showAppointmentModal, embedded]);

  // Calculate date range based on view mode — mirrors CalendarPage logic.
  const dateRange = useMemo(() => {
    const current = new Date(currentDate);
    let start;
    let end;
    if (viewMode === "day") {
      start = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 0, 0, 0);
      end = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 23, 59, 59);
    } else if (viewMode === "week") {
      const dayOfWeek = current.getDay();
      start = new Date(current);
      start.setDate(current.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(current.getFullYear(), current.getMonth(), 1, 0, 0, 0);
      end = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59);
    }
    return { start, end };
  }, [currentDate, viewMode]);

  // Fetch ALL clinic appointments (no therapistId filter → admin view).
  const {
    data: allAppointments = [],
    isLoading,
    refetch,
    isFetching,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    {
      skip: !clinicId,
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    }
  );

  // Team absences in range (multi-clinic v2). Peers see "לא זמין/ה" only;
  // type/note render for admins and the absent therapist themself.
  const { data: rangeAbsences = [] } = useGetAbsencesQuery(
    {
      clinicId,
      from: toLocalDateStr(dateRange.start),
      to: toLocalDateStr(dateRange.end),
    },
    { skip: !clinicId }
  );
  const absences = useMemo(
    () =>
      rangeAbsences.map((a) => ({
        ...a,
        canSeeDetails: canSeeAbsenceDetails(a, { viewerId: adminTherapistId, isAdmin: viewerIsAdmin }),
      })),
    [rangeAbsences, adminTherapistId, viewerIsAdmin]
  );

  // Fetch all clinic therapists (for color-coding + modal dropdown fallback).
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Auto-refresh when the tab regains focus
  useVisibilityRefresh(refetch);

  // Active, non-secretary therapists only — secretaries have no calendar, so
  // they never get a color / legend chip / scheduling target.
  const activeTherapists = useMemo(
    () => clinicTherapists.filter(isAssignableTherapist),
    [clinicTherapists]
  );

  // Stable color mapping: sort by uid so colors don't shuffle between loads.
  const therapistColorMap = useMemo(() => {
    const map = {};
    const sorted = [...activeTherapists].sort((a, b) =>
      (a.uid || "").localeCompare(b.uid || "")
    );
    sorted.forEach((t, idx) => {
      map[t.uid] = {
        color: THERAPIST_COLORS[idx % THERAPIST_COLORS.length],
        name: `${t.firstName || ""} ${t.lastName || ""}`.trim() || t.email || "מטפל",
      };
    });
    return map;
  }, [activeTherapists]);

  // Filter appointments by therapist toggles in the legend.
  const appointments = useMemo(() => {
    if (hiddenTherapistIds.size === 0) return allAppointments;
    return allAppointments.filter((a) => !hiddenTherapistIds.has(a.therapistId));
  }, [allAppointments, hiddenTherapistIds]);

  // Fetch the clinic's full patients list once — used to prime the
  // AppointmentModal dropdown when no therapist is pre-selected.
  // When the admin later picks a therapist in the modal, that modal refetches
  // patients filtered by the chosen therapist.
  // Patients feed the scheduling modal only; a read-only viewer (calendar.viewClinic
  // without manage) may lack patients.viewAllClinic — skip to avoid a denied list query.
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !canManage }
  );

  const toggleTherapistVisibility = useCallback((therapistId) => {
    setHiddenTherapistIds((prev) => {
      const next = new Set(prev);
      if (next.has(therapistId)) next.delete(therapistId);
      else next.add(therapistId);
      return next;
    });
  }, []);

  const showAllTherapists = useCallback(() => {
    setHiddenTherapistIds(new Set());
  }, []);

  // --- Navigation ---
  const goToPrevious = useCallback(() => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() - 1);
    else if (viewMode === "week") next.setDate(next.getDate() - 7);
    else next.setMonth(next.getMonth() - 1);
    setCurrentDate(next);
  }, [currentDate, viewMode]);

  const goToNext = useCallback(() => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() + 1);
    else if (viewMode === "week") next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + 1);
    setCurrentDate(next);
  }, [currentDate, viewMode]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // --- Handlers wired into Day/Week/Month views ---
  const handleSlotClick = (slotDate, dateOnly = false) => {
    // Read-only viewers (calendar.viewClinic without manageOthersAppointments)
    // cannot schedule from the clinic calendar.
    if (!canManage) return;
    setPrefilledDate(slotDate);
    setPrefilledDateOnly(dateOnly);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  const handleAppointmentClick = (appointment) => {
    if (!canManage) {
      showInfo("תצוגה בלבד — התור שייך למטפל/ת אחר/ת.");
      return;
    }
    setSelectedAppointment(appointment);
    setPrefilledDate(null);
    setPrefilledDateOnly(false);
    setShowAppointmentModal(true);
  };

  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setPrefilledDate(null);
    setPrefilledDateOnly(false);
  };

  const handleAppointmentSuccess = () => {
    refetch();
  };

  const handleDayClickFromMonth = (clickedDate) => {
    setCurrentDate(clickedDate);
    setViewMode("day");
  };

  const handleViewDayFromWeek = (clickedDate) => {
    setCurrentDate(clickedDate);
    setViewMode("day");
  };

  const handleAddAppointmentFromHeader = () => {
    if (viewMode === "day") {
      handleSlotClick(new Date(currentDate), true);
    } else {
      handleSlotClick(null, false);
    }
  };

  // Admin context-menu actions from the views (add-task) — open tasks page
  // in a new tab so we don't trash the modal state.
  const handleAddTaskForDate = (selectedDate) => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    window.open(
      `/dashboard/tasks?tab=tasks&action=new&date=${year}-${month}-${day}`,
      "_blank"
    );
  };

  // --- Header date text ---
  const formatHeaderDate = () => {
    const current = new Date(currentDate);
    if (viewMode === "day") {
      return current.toLocaleDateString("he-IL", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    if (viewMode === "week") {
      const weekStart = dateRange.start;
      const weekEnd = dateRange.end;
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekStart.toLocaleDateString(
          "he-IL",
          { month: "long", year: "numeric" }
        )}`;
      }
      const startStr = weekStart.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
      });
      const endStr = weekEnd.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return `${startStr} - ${endStr}`;
    }
    return current.toLocaleDateString("he-IL", {
      month: "long",
      year: "numeric",
    });
  };

  // For the AppointmentModal, pre-select the admin themselves if they are also
  // an active therapist in the clinic (most common case). Otherwise default to
  // the first active therapist. The therapist is fixed for the appointment
  // (no top-level "מטפל אחראי" dropdown inside the modal anymore — the
  // appointment is created for the therapist whose row was clicked, or the
  // default above when entering from the generic "הוסף פגישה" header button).
  const defaultTargetTherapist = useMemo(() => {
    if (!activeTherapists.length) return null;
    const self = activeTherapists.find((t) => t.uid === adminTherapistId);
    return self || activeTherapists[0];
  }, [activeTherapists, adminTherapistId]);

  const totalAppointments = allAppointments.length;
  const visibleAppointments = appointments.length;
  const hiddenCount = hiddenTherapistIds.size;

  const card = (
      <div
        className={`clinic-calendar-modal${embedded ? " clinic-calendar-embedded" : ""}`}
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="clinic-calendar-modal-header">
          <div className="header-right">
            <div className="clinic-calendar-title-block">
              <div className="clinic-calendar-icon">
                <UsersThree size={24} weight="duotone" />
              </div>
              <div className="clinic-calendar-title-text">
                <h2>יומן קליניקה מרוכז</h2>
                <span className="clinic-calendar-subtitle">
                  תצוגה של כל הפגישות בכל יומני המטפלים
                </span>
              </div>
            </div>
          </div>

          <div className="header-center">
            <div className="date-navigation">
              <button className="nav-btn" onClick={goToPrevious} title="הקודם">
                <CaretRight size={18} weight="bold" />
              </button>
              <button className="today-btn" onClick={goToToday}>
                <CalendarBlank size={16} weight="regular" />
                <span>היום</span>
              </button>
              <button className="nav-btn" onClick={goToNext} title="הבא">
                <CaretLeft size={18} weight="bold" />
              </button>
            </div>
            <h3 className="current-date-display">{formatHeaderDate()}</h3>
          </div>

          <div className="header-left">
            <button
              className="refresh-btn"
              onClick={() => refetch()}
              disabled={isFetching}
              title="רענן נתונים"
            >
              <ArrowClockwise
                size={18}
                weight="bold"
                className={isFetching ? "spinning" : ""}
              />
            </button>
            {!embedded && <ModalCloseButton onClick={onClose} />}
          </div>
        </div>

        {!canManage && (
          <div className="clinic-calendar-readonly">
            <ReadOnlyBanner text="תצוגה בלבד — ניתן לצפות ביומן הקליניקה ללא קביעת תורים." />
          </div>
        )}

        {/* Action Row (view toggle + add) */}
        <div className="clinic-calendar-action-row">
          <div className="view-toggle">
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`view-btn ${viewMode === opt.value ? "active" : ""}`}
                onClick={() => setViewMode(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="action-stats">
            <CalendarBlank size={14} weight="fill" />
            {hiddenCount > 0 ? (
              <span>
                מציג <strong>{visibleAppointments}</strong> מתוך {totalAppointments} פגישות
              </span>
            ) : (
              <span>
                <strong>{totalAppointments}</strong> פגישות בתצוגה זו
              </span>
            )}
          </div>

          {canManage && (
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={handleAddAppointmentFromHeader}
              disabled={!defaultTargetTherapist}
              title={
                !defaultTargetTherapist
                  ? "לא נמצאו מטפלים פעילים בקליניקה"
                  : "הוסף פגישה חדשה"
              }
            >
              פגישה חדשה
            </CustomClinikitActionButton>
          )}
        </div>

        {/* Therapist Legend (color chips + toggles) */}
        {activeTherapists.length > 0 && (
          <div className="clinic-calendar-therapist-legend" role="group" aria-label="מטפלים">
            <span className="legend-label-title">
              <PlusCircle size={14} weight="fill" />
              מטפלים:
            </span>
            <div className="legend-chips">
              {activeTherapists.map((t) => {
                const color = therapistColorMap[t.uid]?.color;
                const name = therapistColorMap[t.uid]?.name;
                const hidden = hiddenTherapistIds.has(t.uid);
                return (
                  <button
                    key={t.uid}
                    type="button"
                    className={`therapist-chip ${hidden ? "hidden" : ""}`}
                    onClick={() => toggleTherapistVisibility(t.uid)}
                    title={
                      hidden
                        ? `הצג שוב את הפגישות של ${name}`
                        : `הסתר זמנית את הפגישות של ${name}`
                    }
                  >
                    <span
                      className="therapist-chip-dot"
                      style={{ backgroundColor: color }}
                    />
                    <span className="therapist-chip-name">{name}</span>
                    {hidden ? (
                      <EyeSlash size={12} weight="bold" />
                    ) : (
                      <Eye size={12} weight="bold" />
                    )}
                  </button>
                );
              })}
            </div>
            {hiddenCount > 0 && (
              <button
                type="button"
                className="show-all-btn"
                onClick={showAllTherapists}
              >
                הצג הכל
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`clinic-calendar-modal-body view-${viewMode}`}>
          {isLoading ? (
            <ContentLoader fill message="טוען יומן קליניקה..." />
          ) : activeTherapists.length === 0 ? (
            <div className="clinic-calendar-empty">
              <UsersThree size={40} weight="duotone" />
              <p>לא נמצאו מטפלים פעילים בקליניקה</p>
              <span>הוסיפי מטפלים מהגדרות הקליניקה כדי להתחיל לנהל את היומן המרוכז</span>
            </div>
          ) : (
            <>
              {viewMode === "day" && (
                <DayView
                  date={new Date(currentDate)}
                  appointments={appointments}
                  availabilitySlots={[]}
                  absences={absences}
                  onSlotClick={handleSlotClick}
                  onAppointmentClick={handleAppointmentClick}
                  onAddTask={handleAddTaskForDate}
                  showTherapistName={true}
                  therapistColorMap={therapistColorMap}
                />
              )}
              {viewMode === "week" && (
                <WeekView
                  startDate={dateRange.start}
                  appointments={appointments}
                  availabilitySlots={[]}
                  absences={absences}
                  onSlotClick={handleSlotClick}
                  onAppointmentClick={handleAppointmentClick}
                  onViewDay={handleViewDayFromWeek}
                  onAddTask={handleAddTaskForDate}
                  showTherapistName={true}
                  multilineTherapist={true}
                  therapistColorMap={therapistColorMap}
                  singleDayMode={isMobile}
                  selectedDayIndex={new Date(currentDate).getDay()}
                />
              )}
              {viewMode === "month" && (
                <MonthView
                  date={new Date(currentDate)}
                  appointments={appointments}
                  availabilitySlots={[]}
                  onDayClick={handleDayClickFromMonth}
                  onAppointmentClick={handleAppointmentClick}
                  onCreateAppointment={(d) => handleSlotClick(d, true)}
                  onAddTask={handleAddTaskForDate}
                  showTherapistName={true}
                  therapistColorMap={therapistColorMap}
                />
              )}
            </>
          )}
        </div>

        {/* Footer helper text */}
        <div className="clinic-calendar-modal-footer">
          {canManage ? (
            <>
              <PlusCircle size={14} weight="fill" />
              <span>לחץ על משבצת ריקה כדי ליצור פגישה חדשה</span>
            </>
          ) : (
            <>
              <Eye size={14} weight="fill" />
              <span>תצוגה בלבד — לקביעת תורים עבור הצוות נדרשת הרשאת ניהול פגישות</span>
            </>
          )}
        </div>

        {/* Admin scheduling AppointmentModal */}
        {canManage && showAppointmentModal && (
          <AppointmentModal
            appointment={selectedAppointment}
            prefilledDate={prefilledDate}
            prefilledDateOnly={prefilledDateOnly}
            patients={patientsData?.patients || []}
            onClose={handleCloseAppointmentModal}
            // For NEW appointments: engage admin scheduling mode and pre-select
            // the admin's own therapist record (if they are one) or the first
            // active therapist as the responsible therapist for the new
            // appointment. The modal no longer exposes a top-level "מטפל אחראי"
            // dropdown — the per-appointment / per-series-row therapist
            // override (visible only for multi-therapist patients) is the
            // single way to change the therapist, mirroring the regular
            // CalendarPage popup.
            // For EDIT (selectedAppointment): anchor the modal to the
            // appointment's ORIGINAL therapist so logs, rooms, SMS settings
            // and patient lists all reflect the correct therapist — not the
            // admin who opens the modal.
            targetTherapistId={
              selectedAppointment
                ? selectedAppointment.therapistId || null
                : defaultTargetTherapist?.uid || null
            }
            targetTherapistName={
              selectedAppointment
                ? selectedAppointment.therapistName || null
                : defaultTargetTherapist
                ? `${defaultTargetTherapist.firstName || ""} ${
                    defaultTargetTherapist.lastName || ""
                  }`.trim()
                : null
            }
            scheduledByAdminId={adminTherapistId}
            scheduledByAdminName={adminTherapistName}
            onSuccess={handleAppointmentSuccess}
          />
        )}
      </div>
  );

  // Embedded (page section): no fixed overlay, no backdrop click-to-close.
  if (embedded) return card;

  return (
    <div
      className="clinic-calendar-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !showAppointmentModal) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="יומן קליניקה מרוכז"
    >
      {card}
    </div>
  );
}

export default ClinicCalendarModal;
